import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { RequestService } from '@df/api/request/request.service';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { Status } from '@df/core/status';
import { ImageModule } from '@df/module-image/image.module';
import { Customer } from '@df/session/api/customer';
import { CarouselModule } from '@df/ui-carousel/carousel.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentField } from '@icms/core/content';
import { Nexus } from '@tomandco/nexus-api-types';
import { Swiper, SwiperEvents } from 'swiper/types';
import { LocalCustomerModel } from '../../../customer/customer.model.decorator';
import { IconsOptions } from '../../../styles/cms.config';
import { ActionComponent } from '../../../ui/api/action.component';
import { NAntavo } from '../../antavo/antavo.interface';
import { AntavoService } from '../../antavo/antavo.service';
import { IMissionSuccessContent, MISSION_SUCCESS_FIELDS, MissionSuccessComponent } from '../mission-success/mission-success.component';

declare module '@df/session/customer/customer.interfaces' {
    namespace Customer {
        interface ICustomerData {
            your_interests?: string; // JSON string of selected interests
        }
    }
}

export namespace NFirstMission {
    export const componentId = 'first-mission';

    export type IBlockData = Catalog.IBlockData<IFirstMissionContent>;

    export interface IFirstMissionContent extends IMissionSuccessContent {
        userIcon: string;
        title: string;
        subtitle: string;
        antavoMissionId: string;
        options: IOption[];
        saveButtonText: string;
    }

    export interface IOption {
        icon?: string;
        label: string;
    }

    export interface IResolverData {
        mission?: NAntavo.IChallenge;
        cmsBlock?: any;
    }
}

@Component({
    selector: 'first-mission',
    templateUrl: './first-mission.component.html',
    styleUrls: ['./first-mission.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        TranslationModule,
        ICmsClientModule,
        RouterModule,
        CarouselModule,
        ImageModule,
        ActionComponent,
        // ResultComponent,
        MissionSuccessComponent
    ]
})
@ICmsComponent<FirstMissionComponent>({
    id: NFirstMission.componentId,
    block: {
        url: NFirstMission.componentId
    },
    onDemandOnly: true,
    name: 'First Mission',
    fields: [
        <NICmsContentField.IChoiceField>{
            key: 'userIcon',
            title: 'User Icon',
            type: NICmsContentField.EFieldType.choice,
            options: IconsOptions,
            previewMode: 'icon',
            default: 'icon-profile'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'title',
            title: 'Title',
            default: 'What are your interests?',
            required: true
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'subtitle',
            title: 'Subtitle',
            default: "I'm interested in:",
            required: true
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'antavoMissionId',
            title: 'Antavo Mission ID',
            required: true,
            description: 'The ID of the Antavo mission to display',
            default: '686e7ed3f0be1554720f18cd'
        },
        <NICmsContentField.IRepeaterField>{
            key: 'options',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Interest Options',
            required: true,
            itemName: 'Option',
            itemNameField: 'label',
            defaultSize: 3,
            fields: [
                <NICmsContentField.IChoiceField>{
                    key: 'icon',
                    title: 'Icon',
                    type: NICmsContentField.EFieldType.choice,
                    options: IconsOptions,
                    previewMode: 'icon',
                    default: 'icon-star'
                },
                {
                    key: 'label',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Label',
                    required: true
                }
            ],
            default: [
                {
                    icon: 'icon-outfit',
                    label: 'Clothing'
                },
                {
                    icon: 'icon-flyhood',
                    label: 'Horse Wear'
                },
                {
                    icon: 'icon-toys',
                    label: 'Toys'
                }
            ]
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'saveButtonText',
            title: 'Save Button Text',
            default: 'Save',
            required: true
        },
        ...MISSION_SUCCESS_FIELDS
    ]
})
export class FirstMissionComponent extends UiComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NFirstMission.IFirstMissionContent;

    protected mission!: NAntavo.IChallenge;
    protected selectedOptions: string[] = [];
    protected swiper?: Swiper;
    protected continueUrl: string = '/account/my-missions';
    protected saveStatus = new Status();

    private route = inject(ActivatedRoute);
    private requestService = inject(RequestService);
    private customer = inject(Customer);
    private antavoService = inject(AntavoService);

    override ngOnInit() {
        super.ngOnInit();
        const resolverData = this.route.snapshot.data['firstMission'] as NFirstMission.IResolverData | undefined;

        if (resolverData?.mission) {
            this.mission = resolverData.mission;
        }
    }

    protected onAfterSwiperInit(eventParams: Parameters<SwiperEvents['afterInit']>): void {
        const [swiper] = eventParams;
        this.swiper = swiper;
    }

    protected toggleOption(optionValue: string): void {
        const index = this.selectedOptions.indexOf(optionValue);
        if (index > -1) {
            this.selectedOptions.splice(index, 1);
        } else {
            this.selectedOptions.push(optionValue);
        }
        this.detectChanges();
    }

    protected isOptionSelected(optionValue: string): boolean {
        return this.selectedOptions.includes(optionValue);
    }

    protected async saveSelection(): Promise<void> {
        if (this.selectedOptions.length === 0) {
            this.saveStatus.error = 'Please select at least one option';
            return;
        }

        this.saveStatus.busy = true;

        try {
            // Save selected interests to customer attributes
            const customerModel = this.customer.model as LocalCustomerModel;
            const interestsJson = JSON.stringify({ interests: this.selectedOptions });

            await this.requestService.submitForm(undefined, undefined, {
                path: Nexus.Api.Customer.Save.path,
                data: {
                    your_interests: interestsJson
                }
            });

            // Send profile_completion event to Antavo to track mission completion
            if (customerModel.loyaltyId) {
                await this.antavoService.finishSimpleMission(this.mission, 'your_interests', this.saveStatus);
            }

            this.swiper?.slideNext();
        } catch (error) {
            console.error('Error saving selection:', error);
            this.saveStatus.busy = false;
            this.saveStatus.error = error as string;
            // You might want to show an error message to the user here
        } finally {
            this.saveStatus.busy = false;
        }
    }
}
